import { useState, useEffect, DependencyList } from 'react';
import { isEqual } from 'lodash';
import { useAppDispatch, useObjectSelector } from '@redux/store';
import { fetchData } from '@redux/slices/functionals/request/requestActions';
import { useTheme } from '@mui/material/styles';
import { CircularProgress } from '@mui/material';
import { WrapperEntitySelector } from '@components/Inputs';
import { requestActions } from '@redux/slices/functionals/request/requestSlice';
import { Icon, IconButton } from '@ts/storybook';
import { globalActions } from '@redux/slices/states/global/globalSlice';
import { Grid, TextInput, GridContainerFullHeight, Typography } from '@ts/storybook';
import {
  ELocationRuleStatus,
  ILocation,
  ILocationType,
  CElement,
  CElementType,
  AllocationCheckResultValueType,
  CLocationRule,
  createLocRuleFromInterface,
  IElementType,
  IForm,
} from '@ts/ui-models';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';

interface ILocationRulesProps extends IForm {
  values: CElement | CElementType;
  isItem?: boolean;
  isItemType?: boolean;
  isQuickAccess?: boolean;
}

/* Is a list of locations rules and form for create locations rules */
/* Two aproaches to create and see from item or itemType */
const LocationRules = (props: ILocationRulesProps) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { isItem, isItemType, values, mode } = props;

  const newLocationRuleId = 'newRuleSet';
  const defaultLocationRule = CLocationRule.generateDefaultValues(newLocationRuleId);
  const [newLocationRule, setNewLocationRule] = useState<CLocationRule>(structuredClone(defaultLocationRule));

  const [rulesList, setRulesList] = useState<CLocationRule[]>(props.values?.locationRules ?? []);

  const {
    data: locationRules,
    loading,
    error,
  }: { data: ILocation | ILocationType; loading: boolean; error: any } = useObjectSelector(
    state => state.request['locationRules']
  );

  const onChangeLocationRule = (
    itemTypeId: string | undefined,
    attr: keyof Pick<CLocationRule, 'expectedValue' | 'aboveVariance' | 'belowVariance'>,
    value: any
  ) => {
    if (!itemTypeId) return;
    let updatedValue: number = value;
    if (['expectedValue', 'aboveVariance', 'belowVariance'].includes(attr)) {
      updatedValue = parseFloat(updatedValue.toString());
      if (attr === 'expectedValue' && !checkExpectedValue(updatedValue)) return;
    }

    if (itemTypeId === newLocationRuleId) {
      const copy = structuredClone(newLocationRule);
      copy[attr] = updatedValue;

      setNewLocationRule(copy);
    } else {
      setRulesList(
        rulesList.map(item => {
          if (item.itemTypeId === itemTypeId) {
            const updatedLocRule = structuredClone(item);
            updatedLocRule[attr] = updatedValue;
            updatedLocRule.status = ELocationRuleStatus.UPDATED;
            return updatedLocRule;
          }
          return item;
        })
      );
    }
  };

  const onClickDeleteLocationRule = (row: CLocationRule) => {
    const updatedList: CLocationRule[] = rulesList.map(item => {
      if (item.itemTypeId === row.itemTypeId) {
        const copy = structuredClone(item);
        copy.status = ELocationRuleStatus.DELETE;
        return copy;
      }
      return item;
    });

    setRulesList(updatedList);
  };
  const onClickAddLocationRule = () => {
    const copy = structuredClone(newLocationRule);
    copy.name = copy.itemType?.name;
    copy.expectedValue = copy.expectedValue || 1;
    copy.aboveVariance = copy.aboveVariance || 0;
    copy.belowVariance = copy.belowVariance || 0;

    if (!checkExpectedValue(copy.expectedValue)) return;
    setRulesList([copy, ...rulesList]);

    const copyDefault = CLocationRule.generateDefaultValues(newLocationRuleId + rulesList.length);
    newLocationRule.aboveVariance = 0;
    newLocationRule.belowVariance = 0;
    setNewLocationRule(copyDefault);
  };

  const handleEntitySelected = (entity: CElementType) => {
    const copy = structuredClone(newLocationRule);
    if (entity) {
      const entityId = entity.getID();

      if (rulesList.some(v => v.itemTypeId === entityId)) {
        copy.itemTypeId = undefined;
        copy.itemType = undefined;
        setNewLocationRule(copy);
        dispatch(
          globalActions.updateGlobalState({
            key: 'notification',
            value: `This id ${entity?.getVisualizationID()} already exists`,
          })
        );
      } else {
        copy.itemTypeId = entityId;
        copy.itemType = entity;
        setNewLocationRule(copy);
      }
    } else {
      copy.itemTypeId = undefined;
      copy.itemType = undefined;
      setNewLocationRule(copy);
    }
  };
  //#endregion

  //#region Functions
  const checkExpectedValue = (expectedValue: number): boolean => {
    if (expectedValue === 0) {
      dispatch(globalActions.updateGlobalState({ key: 'notification', value: 'Expected value must be greater than 0' }));
      return false;
    }
    return true;
  };

  const isViewMode = () => {
    return props.mode === 'view';
  };

  const getItemFoundInItemsInsideByType = (item: CLocationRule): number => {
    let numItemFound: number = 0;

    if (isItem) {
      const locRules = locationRules;

      if (locRules && 'itemsInsideByType' in locRules) {
        //IS ILocation
        numItemFound = locRules.itemsInsideByType.find(v => v.itemTypeId === item?.itemTypeId)?.count ?? 0;
      }
    } else {
      numItemFound = item.itemsCount ?? 0;
    }

    return numItemFound;
  };

  const getStatusLocationColor = (item: CLocationRule) => {
    if (item.resultType) {
      switch (item.resultType) {
        case AllocationCheckResultValueType.Ok:
          return theme.palette.success;
        case AllocationCheckResultValueType.InVariance:
          return theme.palette.warning;
        case AllocationCheckResultValueType.Fail:
          return theme.palette.error;
        default:
      }
    }

    const itemsFound = getItemFoundInItemsInsideByType(item);

    const requiredFulfilled = itemsFound === item.expectedValue;

    const aboveFulfilled = itemsFound <= (item.expectedValue || 0) + (item.aboveVariance || 0);

    const belowFulfilled = itemsFound >= (item.expectedValue || 0) - (item.belowVariance || 0);

    const globalColor = () => {
      if (!requiredFulfilled && aboveFulfilled && belowFulfilled) {
        return theme.palette.warning;
      } else if (!requiredFulfilled) {
        return theme.palette.error;
      }
      if (requiredFulfilled && (!aboveFulfilled || !belowFulfilled)) {
        return theme.palette.warning;
      }
      return theme.palette.success;
    };

    return globalColor();
  };

  const transformLocationRulesToListRules = () => {
    if (isItemType || isItem) {
      const transform = locationRules.locationRules
        .map(v => {
          return createLocRuleFromInterface(v);
        })
        .map(v => {
          v.itemType = new CElementType(values as IElementType);
          return v;
        });
      setRulesList(transform);
    }
  };
  const getLocationRules = () => {
    if ((isItemType || isItem) && mode !== 'new') {
      dispatch(
        fetchData({
          key: 'locationRules',
          url: isItemType ? `/api/v2/locationType/${values?.logicalId}` : `/api/v2/location/${values?.logicalId}`,
        })
      );
    }
  };
  //#endregion

  //#region Render
  const renderHeaderLocationsRulesList = () => {
    return (
      <Grid id="header-location-rules" container mb={2} pl={2} size={12}>
        <Grid container size={12} spacing={2} justifyContent="space-evenly">
          <Grid container size={{ xs: 3 }} justifyContent="flex-start" alignItems="center">
            <Typography className="ml-2" variant="h6" text="Expected value" />
          </Grid>
          {(isItem || props.isQuickAccess) && (
            <Grid container size={{ xs: 2 }} justifyContent="center" alignItems="center">
              <Typography className="ml-2" variant="h6" text="Items found" />
            </Grid>
          )}
          <Grid container size={{ xs: 2 }} justifyContent="center" alignItems="center">
            <Icon icon="symbols_equal_outlined" size={25} />
            <Typography className="ml-2" variant="h6" text="Expected value" />
          </Grid>
          <Grid container size={{ xs: 2 }} justifyContent="center" alignItems="center">
            <Icon icon="keyboard_double_arrow_down" size={25} />
            <Typography className="ml-2" variant="h6" text="Below variance" />
          </Grid>
          <Grid container size={{ xs: 2 }} justifyContent="center" alignItems="center">
            <Icon icon="keyboard_double_arrow_up" size={25} />
            <Typography className="ml-2" variant="h6" text="Above variance" />
          </Grid>
          {!isViewMode() && <Grid size={{ xs: 1 }} />}
        </Grid>
      </Grid>
    );
  };
  const renderFormNewLocationRules = () => {
    if (props.mode === 'view') return null;
    return (
      <Grid id={'form-location-rules'} container pl={2} pt={1} pb={1} size={12}>
        <Grid container spacing={2} size={12} sx={{ alignItems: 'center', justifyContent: 'space-evenly' }}>
          <Grid size={{ xs: 3 }} sx={{ paddingBottom: '16px' }}>
            <WrapperEntitySelector
              label="Type of item"
              value={newLocationRule.itemTypeId}
              onChangeEntity={v => {
                handleEntitySelected(v as CElementType);
              }}
              searchComponent={generateSearchEntitySelector('itemType')}
            />
          </Grid>
          {(isItem || props.isQuickAccess) && (
            <Grid size={{ xs: 2 }}>
              <Typography variant="body2" />
            </Grid>
          )}
          <Grid size={{ xs: 2 }}>
            <TextInput
              type="number"
              min={1}
              value={newLocationRule.expectedValue}
              placeholder="0"
              isReadOnly={isViewMode()}
              onChangeValue={v => onChangeLocationRule(newLocationRuleId, 'expectedValue', v)}
            />
          </Grid>
          <Grid size={{ xs: 2 }}>
            <TextInput
              type="number"
              min={0}
              value={newLocationRule.belowVariance}
              placeholder="0"
              isReadOnly={isViewMode()}
              onChangeValue={v => onChangeLocationRule(newLocationRuleId, 'belowVariance', v)}
            />
          </Grid>
          <Grid size={{ xs: 2 }}>
            <TextInput
              type="number"
              min={0}
              value={newLocationRule.aboveVariance}
              placeholder="0"
              isReadOnly={isViewMode()}
              onChangeValue={v => onChangeLocationRule(newLocationRuleId, 'aboveVariance', v)}
            />
          </Grid>
          {newLocationRule.itemTypeId ? (
            <Grid size={{ xs: 1 }}>
              <IconButton icon="addBox" onClick={onClickAddLocationRule} />
            </Grid>
          ) : (
            <Grid size={{ xs: 1 }} />
          )}
        </Grid>
      </Grid>
    );
  };
  const renderLocationsRulesList = () => {
    if (loading) {
      return (
        <Grid container size={{ xs: 10 }} justifyContent="center">
          <CircularProgress />
        </Grid>
      );
    }
    if (error) {
      return;
    }
    if (rulesList.length !== 0) {
      return rulesList
        .filter(row => row.status !== ELocationRuleStatus.DELETE)
        .map((row, i) => {
          return (
            <Grid
              id={'row-location-rules' + i}
              container
              size={12}
              key={row.itemTypeId}
              sx={{
                backgroundColor:
                  isItem || props.isQuickAccess ? getStatusLocationColor(row).light : i % 2 === 0 ? '#f5f5f5' : 'white',
                color: isItem || props.isQuickAccess ? getStatusLocationColor(row).contrastText : 'black',
                height: '50px',
              }}
              pl={2}
              py={1}
              alignItems="flex-start"
            >
              <Grid container spacing={2} alignItems="center" justifyContent="space-evenly" size={12}>
                <Grid size={{ xs: 3 }} className="!pt-0 mt-4">
                  <Typography
                    variant="body1"
                    text={[row.name, <span className="text-gray-400">{[' (', row.itemType?.getVisualizationID(), ')']}</span>]}
                  />
                </Grid>
                {(isItem || props.isQuickAccess) && (
                  <Grid size={{ xs: 2 }}>
                    <Typography variant="body1" className="text-center" text={getItemFoundInItemsInsideByType(row)} />
                  </Grid>
                )}
                <Grid size={{ xs: 2 }}>
                  <TextInput
                    type="number"
                    min={1}
                    value={row.expectedValue}
                    isReadOnly={isViewMode()}
                    onChangeValue={v => onChangeLocationRule(row.itemTypeId, 'expectedValue', v)}
                  />
                </Grid>
                <Grid size={{ xs: 2 }}>
                  <TextInput
                    type="number"
                    min={0}
                    value={row.belowVariance}
                    isReadOnly={isViewMode()}
                    onChangeValue={v => onChangeLocationRule(row.itemTypeId, 'belowVariance', v)}
                  />
                </Grid>
                <Grid size={{ xs: 2 }}>
                  <TextInput
                    type="number"
                    min={0}
                    value={row.aboveVariance}
                    isReadOnly={isViewMode()}
                    onChangeValue={v => onChangeLocationRule(row.itemTypeId, 'aboveVariance', v)}
                  />
                </Grid>
                {!isViewMode() && (
                  <Grid size={{ xs: 1 }}>
                    <IconButton icon="delete" onClick={() => onClickDeleteLocationRule(row)} />
                  </Grid>
                )}
              </Grid>
            </Grid>
          );
        });
    }
  };
  //#endregion

  //#region Effects
  const effectLoadAndDestroyLocationRules = (deps: DependencyList) => {
    useEffect(() => {
      if (!props.values?.locationRules) getLocationRules();
      return () => {
        dispatch(requestActions.cleanKey('locationRules'));
      };
    }, deps);
  };
  const effectAfterLoadLocationRules = (deps: DependencyList) => {
    useEffect(() => {
      if (locationRules) {
        transformLocationRulesToListRules();
      }
    }, deps);
  };

  const effectUpdateLocationRules = (deps: DependencyList) => {
    useEffect(() => {
      if (!isEqual(props.values?.locationRules, rulesList)) {
        //Every change in rulesList send update to curValue
        props.onChange?.('locationRules', rulesList);
      }
    }, deps);
  };
  //#endregion

  effectLoadAndDestroyLocationRules([dispatch]);
  effectAfterLoadLocationRules([locationRules]);
  effectUpdateLocationRules([rulesList]);

  return (
    <GridContainerFullHeight id="location-rules-tab" pt={2} alignItems="flex-start">
      <Grid container size={{ xs: 12 }} sx={{ height: 'auto' }}>
        {renderHeaderLocationsRulesList()}
        {renderFormNewLocationRules()}
        {renderLocationsRulesList()}
      </Grid>
    </GridContainerFullHeight>
  );
};

export default LocationRules;
